/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import CardActions from '@mui/material/CardActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import db from 'services/Firebase';
import PosCart from './PosCart';

function DashboardPosCart() {
  const mod = 'dashboard_pos';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [cart, setCart] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const txtSearch = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    FetchData('POST', `/${mod}/cart`, app.token, {
      lang: app.lang,
      search: txtSearch.current.value,
    }).then((result) => {
      if (result.status === 'ok') {
        setCart(result.data);
      } else {
        Func.exitApp();
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const btnNote = (item) => {
    return (
      <Tooltip title='Notd'>
        <IconButton aria-label='report_problem' color='secondary' size='small'>
          <Icon>description</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    return (
      <Tooltip title='delete'>
        <IconButton
          aria-label='settings'
          size='small'
          color='error'
          sx={{ marginLeft: 'auto' }}
          onClick={() => del(item)}
        >
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const renderSubHeader = (item) => {
    if (item.kind === 'KEEP') {
      return Func.codeStatus('จัดเก็บ', 'danger');
    }

    return (
      <Tooltip title='ยอดเงินในลิ้นชัก'>
        <code className='color-info nowrap'>{Func.numberDisplay(item.wallet)} ฿</code>
      </Tooltip>
    );
  };

  const renderTitle = (item) => {
    return (
      <Tooltip title={item.branch_name}>
        <MDTypography variant='subtitle2'>{item.branch_code}</MDTypography>
      </Tooltip>
    );
  };

  const renderCart = () => {
    return (
      <Card sx={{ p: 2, mb: 2 }}>
        <MDBox mb={1}>
          <Grid container>
            <Grid item xs={6}>
              <MDTypography variant='h5'>POS</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='gradient' color='light' size='small' iconOnly onClick={view}>
                  <Icon>refresh</Icon>
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view();
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                type='search'
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          {cart.map((row) => (
            <Grid item xs={12} md={6} lg={3} xl={2} key={row.id}>
              <MDBox className='card'>
                <CardHeader
                  avatar={Func.imageDisplay(row)}
                  title={renderTitle(row)}
                  subheader={renderSubHeader(row)}
                  action={<PosCart parent={row} />}
                />
                <CardContent sx={{ px: 2, py: 0, fontSize: 15, color: '#666666', fontWeight: 100 }}>
                  <Grid container>
                    <Grid item xs={2}>
                      <Icon>shopping_bag</Icon>
                    </Grid>
                    <Grid item xs={10} className='text-right color-danger'>
                      {Func.numberDisplay(row.nettotal)} ฿
                    </Grid>
                    <Grid item xs={2}>
                      <Icon>point_of_sale</Icon>
                    </Grid>
                    <Grid item xs={10} className='text-right color-primary'>
                      {row.cashier_name === '' ? '-' : row.cashier_name}
                    </Grid>
                    <Grid item xs={2}>
                      <Icon>date_range</Icon>
                    </Grid>
                    <Grid item xs={10} className='text-right'>
                      {row.shift_code === '' ? '-' : row.shift_code}
                    </Grid>
                    <Grid item xs={2}>
                      <Icon>person</Icon>
                    </Grid>
                    <Grid item xs={10} className='text-right'>
                      {row.user_name === '' ? '-' : row.user_name}
                    </Grid>
                    <Grid item xs={2}>
                      <Icon>schedule</Icon>
                    </Grid>
                    <Grid item xs={10} className='text-right'>
                      {Func.dateTimeDisplay(row.updated_at)}
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions disableSpacing>
                  {btnNote(row)}
                  {btnDel(row)}
                </CardActions>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, []);

  return (
    <div>
      {renderCart()}
      {renderLoading()}
      {notify}
    </div>
  );
}

export default DashboardPosCart;
